import React, { useState, useEffect } from 'react';
import { theme } from '../styles/theme';
import { fetchPlans, createPlan, updatePlan, deletePlan } from '../services/firebase';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

interface Plan {
  id: string;
  days: number;
  device: number;
  value: number;
}

interface PlanFormData {
  days: number;
  device: number;
  value: number;
}

const Plans: React.FC = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<PlanFormData>({ days: 0, device: 0, value: 0 });
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState<PlanFormData>({ days: 0, device: 0, value: 0 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      setLoading(true);
      const data = await fetchPlans();
      setPlans(data);
    } catch (error) {
      console.error('Erro ao carregar planos:', error);
      alert('Erro ao carregar planos');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (plan: Plan) => {
    setEditingId(plan.id);
    setEditForm({
      days: plan.days,
      device: plan.device,
      value: plan.value,
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({ days: 0, device: 0, value: 0 });
  };

  const handleSaveEdit = async (planId: string) => {
    if (editForm.days <= 0 || editForm.device <= 0 || editForm.value <= 0) {
      alert('Todos os campos devem ter valores maiores que zero');
      return;
    }

    setSaving(true);
    try {
      await updatePlan(planId, editForm);
      await loadPlans();
      setEditingId(null);
      setEditForm({ days: 0, device: 0, value: 0 });
    } catch (error) {
      console.error('Erro ao atualizar plano:', error);
      alert('Erro ao atualizar plano');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (planId: string) => {
    if (!window.confirm('Deseja realmente excluir este plano?')) {
      return;
    }

    setSaving(true);
    try {
      await deletePlan(planId);
      await loadPlans();
    } catch (error) {
      console.error('Erro ao excluir plano:', error);
      alert('Erro ao excluir plano');
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    if (createForm.days <= 0 || createForm.device <= 0 || createForm.value <= 0) {
      alert('Todos os campos devem ter valores maiores que zero');
      return;
    }

    setSaving(true);
    try {
      await createPlan(createForm);
      await loadPlans();
      setShowCreateForm(false);
      setCreateForm({ days: 0, device: 0, value: 0 });
    } catch (error) {
      console.error('Erro ao criar plano:', error);
      alert('Erro ao criar plano');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loading}>Carregando planos...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <CardMembershipIcon style={{ fontSize: 36, color: theme.colors.primary }} />
          <h1 style={styles.title}>Gerenciar Planos</h1>
        </div>
        <button
          style={styles.addButton}
          onClick={() => setShowCreateForm(!showCreateForm)}
          title="Adicionar novo plano"
        >
          <AddIcon style={{ fontSize: 24 }} />
        </button>
      </header>

      <div style={styles.content}>
        {/* Formulário de Criação */}
        {showCreateForm && (
          <div style={styles.createFormCard}>
            <h2 style={styles.formTitle}>Novo Plano</h2>
            <div style={styles.formGrid}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>
                  <CalendarTodayIcon style={{ fontSize: 18, marginRight: 8 }} />
                  Dias
                </label>
                <input
                  type="number"
                  value={createForm.days}
                  onChange={(e) => setCreateForm({ ...createForm, days: parseInt(e.target.value) || 0 })}
                  style={styles.input}
                  placeholder="Ex: 183"
                  min="1"
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>
                  <PhoneAndroidIcon style={{ fontSize: 18, marginRight: 8 }} />
                  Dispositivos
                </label>
                <input
                  type="number"
                  value={createForm.device}
                  onChange={(e) => setCreateForm({ ...createForm, device: parseFloat(e.target.value) || 0 })}
                  step="0.01"
                  style={styles.input}
                  placeholder="Ex: 23.99"
                  min="0.01"
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>
                  <AttachMoneyIcon style={{ fontSize: 18, marginRight: 8 }} />
                  Valor (R$)
                </label>
                <input
                  type="number"
                  value={createForm.value}
                  onChange={(e) => setCreateForm({ ...createForm, value: parseFloat(e.target.value) || 0 })}
                  step="0.01"
                  style={styles.input}
                  placeholder="Ex: 71.99"
                  min="0.01"
                />
              </div>
            </div>
            <div style={styles.formActions}>
              <button
                onClick={handleCreate}
                disabled={saving}
                style={styles.saveButton}
              >
                <SaveIcon style={{ fontSize: 20, marginRight: 8 }} />
                {saving ? 'Salvando...' : 'Criar Plano'}
              </button>
              <button
                onClick={() => {
                  setShowCreateForm(false);
                  setCreateForm({ days: 0, device: 0, value: 0 });
                }}
                disabled={saving}
                style={styles.cancelButton}
              >
                <CancelIcon style={{ fontSize: 20, marginRight: 8 }} />
                Cancelar
              </button>
            </div>
          </div>
        )}

        {/* Lista de Planos */}
        <div style={styles.plansGrid}>
          {plans.length === 0 ? (
            <div style={styles.emptyState}>
              <CardMembershipIcon style={{ fontSize: 64, color: theme.colors.tertiary, opacity: 0.5 }} />
              <p style={styles.emptyText}>Nenhum plano cadastrado</p>
              <p style={styles.emptySubtext}>Clique no botão + para adicionar um novo plano</p>
            </div>
          ) : (
            plans.map((plan) => (
              <div key={plan.id} style={styles.planCard}>
                {editingId === plan.id ? (
                  // Modo de Edição
                  <div style={styles.editMode}>
                    <h3 style={styles.cardTitle}>Editando Plano</h3>
                    <div style={styles.editFormGrid}>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>
                          <CalendarTodayIcon style={{ fontSize: 16 }} /> Dias
                        </label>
                        <input
                          type="number"
                          value={editForm.days}
                          onChange={(e) => setEditForm({ ...editForm, days: parseInt(e.target.value) || 0 })}
                          style={styles.input}
                          min="1"
                        />
                      </div>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>
                          <PhoneAndroidIcon style={{ fontSize: 16 }} /> Dispositivos
                        </label>
                        <input
                          type="number"
                          value={editForm.device}
                          onChange={(e) => setEditForm({ ...editForm, device: parseFloat(e.target.value) || 0 })}
                          step="0.01"
                          style={styles.input}
                          min="0.01"
                        />
                      </div>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>
                          <AttachMoneyIcon style={{ fontSize: 16 }} /> Valor
                        </label>
                        <input
                          type="number"
                          value={editForm.value}
                          onChange={(e) => setEditForm({ ...editForm, value: parseFloat(e.target.value) || 0 })}
                          step="0.01"
                          style={styles.input}
                          min="0.01"
                        />
                      </div>
                    </div>
                    <div style={styles.cardActions}>
                      <button
                        onClick={() => handleSaveEdit(plan.id)}
                        disabled={saving}
                        style={styles.saveIconButton}
                        title="Salvar"
                      >
                        <SaveIcon style={{ fontSize: 20 }} />
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        disabled={saving}
                        style={styles.cancelIconButton}
                        title="Cancelar"
                      >
                        <CancelIcon style={{ fontSize: 20 }} />
                      </button>
                    </div>
                  </div>
                ) : (
                  // Modo de Visualização
                  <>
                    <div style={styles.cardHeader}>
                      <h3 style={styles.planName}>{plan.id}</h3>
                      <div style={styles.cardActions}>
                        <button
                          onClick={() => handleEdit(plan)}
                          disabled={saving}
                          style={styles.editIconButton}
                          title="Editar"
                        >
                          <EditIcon style={{ fontSize: 20 }} />
                        </button>
                        <button
                          onClick={() => handleDelete(plan.id)}
                          disabled={saving}
                          style={styles.deleteIconButton}
                          title="Excluir"
                        >
                          <DeleteIcon style={{ fontSize: 20 }} />
                        </button>
                      </div>
                    </div>
                    <div style={styles.cardBody}>
                      <div style={styles.planInfo}>
                        <div style={styles.infoIcon}>
                          <CalendarTodayIcon style={{ fontSize: 20, color: theme.colors.secondary }} />
                        </div>
                        <div>
                          <div style={styles.infoLabel}>Dias</div>
                          <div style={styles.infoValue}>{plan.days}</div>
                        </div>
                      </div>
                      <div style={styles.planInfo}>
                        <div style={styles.infoIcon}>
                          <PhoneAndroidIcon style={{ fontSize: 20, color: theme.colors.secondary }} />
                        </div>
                        <div>
                          <div style={styles.infoLabel}>Dispositivos</div>
                          <div style={styles.infoValue}>{plan.device}</div>
                        </div>
                      </div>
                      <div style={styles.planInfo}>
                        <div style={styles.infoIcon}>
                          <AttachMoneyIcon style={{ fontSize: 20, color: '#4CAF50' }} />
                        </div>
                        <div>
                          <div style={styles.infoLabel}>Valor</div>
                          <div style={styles.infoValue}>R$ {plan.value.toFixed(2)}</div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    background: '#f5f5f5',
  },
  header: {
    background: theme.colors.white,
    padding: '25px 40px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
  },
  title: {
    color: theme.colors.primary,
    margin: 0,
    fontSize: '28px',
    fontWeight: 'bold',
  },
  addButton: {
    padding: '12px',
    borderRadius: '8px',
    border: `2px solid ${theme.colors.primary}`,
    background: theme.colors.primary,
    color: theme.colors.white,
    fontSize: '24px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s',
    minWidth: '48px',
    minHeight: '48px',
  },
  content: {
    padding: '40px',
  },
  createFormCard: {
    background: theme.colors.white,
    padding: '30px',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    marginBottom: '30px',
    border: `2px solid ${theme.colors.primary}`,
  },
  formTitle: {
    color: theme.colors.primary,
    fontSize: '22px',
    marginBottom: '20px',
    fontWeight: 'bold',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px',
    marginBottom: '20px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: theme.colors.secondary,
    display: 'flex',
    alignItems: 'center',
  },
  input: {
    padding: '12px',
    borderRadius: '8px',
    border: `2px solid ${theme.colors.tertiary}`,
    fontSize: '16px',
    outline: 'none',
    transition: 'border-color 0.3s',
  },
  formActions: {
    display: 'flex',
    gap: '15px',
    justifyContent: 'flex-end',
  },
  saveButton: {
    padding: '12px 24px',
    borderRadius: '8px',
    border: 'none',
    background: '#4CAF50',
    color: theme.colors.white,
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    transition: 'all 0.3s',
  },
  cancelButton: {
    padding: '12px 24px',
    borderRadius: '8px',
    border: `2px solid ${theme.colors.tertiary}`,
    background: 'transparent',
    color: theme.colors.secondary,
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    transition: 'all 0.3s',
  },
  plansGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '25px',
  },
  planCard: {
    background: theme.colors.white,
    padding: '25px',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    transition: 'transform 0.3s, box-shadow 0.3s',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    paddingBottom: '15px',
    borderBottom: `2px solid ${theme.colors.tertiary}`,
  },
  planName: {
    color: theme.colors.primary,
    fontSize: '20px',
    fontWeight: 'bold',
    margin: 0,
  },
  cardActions: {
    display: 'flex',
    gap: '10px',
  },
  editIconButton: {
    padding: '10px',
    borderRadius: '6px',
    border: 'none',
    background: theme.colors.secondary,
    color: theme.colors.white,
    fontSize: '20px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '40px',
    minHeight: '40px',
    transition: 'all 0.3s',
  },
  deleteIconButton: {
    padding: '10px',
    borderRadius: '6px',
    border: 'none',
    background: '#f44336',
    color: 'white',
    fontSize: '20px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '40px',
    minHeight: '40px',
    transition: 'all 0.3s',
  },
  cardBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  },
  planInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    padding: '15px',
    background: '#f9f9f9',
    borderRadius: '8px',
  },
  infoIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  infoLabel: {
    fontSize: '12px',
    color: theme.colors.secondary,
    fontWeight: 'bold',
    textTransform: 'uppercase',
    marginBottom: '4px',
  },
  infoValue: {
    fontSize: '20px',
    color: theme.colors.text,
    fontWeight: 'bold',
  },
  editMode: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  },
  cardTitle: {
    color: theme.colors.primary,
    fontSize: '18px',
    marginBottom: '10px',
    fontWeight: 'bold',
  },
  editFormGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  },
  saveIconButton: {
    padding: '10px',
    borderRadius: '6px',
    border: 'none',
    background: '#4CAF50',
    color: 'white',
    fontSize: '20px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '40px',
    minHeight: '40px',
    transition: 'all 0.3s',
  },
  cancelIconButton: {
    padding: '10px',
    borderRadius: '6px',
    border: 'none',
    background: '#ff9800',
    color: 'white',
    fontSize: '20px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '40px',
    minHeight: '40px',
    transition: 'all 0.3s',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    background: '#f5f5f5',
  },
  loading: {
    color: theme.colors.primary,
    fontSize: '24px',
    fontWeight: 'bold',
  },
  emptyState: {
    gridColumn: '1 / -1',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    textAlign: 'center',
  },
  emptyText: {
    fontSize: '20px',
    color: theme.colors.secondary,
    fontWeight: 'bold',
    marginTop: '20px',
    marginBottom: '10px',
  },
  emptySubtext: {
    fontSize: '16px',
    color: theme.colors.tertiary,
  },
};

export default Plans;
